import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { checklistsApi, typesApi, inspectionsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, 
  Loader2, 
  Building2, 
  User, 
  MapPin, 
  Phone, 
  FileText, 
  ClipboardList, 
  Calendar, 
  Mail,
  Shield,
  Bed,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// Standard-Werte für Mittlere Höhe Extras
const DEFAULT_MITTLERE_HOEHE_EXTRAS = {
  sicherheitsbeauftragter: {
    name: '',
    adresse: '',
    plz_ort: '',
    mobile: '',
    email: ''
  },
  objekt_daten: {
    objekt: '',
    dossier_nr: '',
    baujahr: '',
    plan_parzelle: '',
    eigentuemer: '',
    abwart_verantwortlicher: '',
    telefon: '',
    anwesende_personen: ''
  },
  inspection_reason: {
    periodisch: true,
    nach_instandstellung: false
  },
  existing_documents: {
    brandschutzkonzept: null,
    letzter_bericht_datum: ''
  },
  building_definition: {
    gebaeudeabstand: null,
    feuerwehrzufahrt: 'ja',
    hydranten: null,
    bauart: 'RF1',
    geschosse_ug: 0,
    geschosse_eg: 1,
    geschosse_og: 0,
    geschosse_dg: 0,
    gaestebetten_ug: 0,
    gaestebetten_eg: 0,
    gaestebetten_og: 0,
    gaestebetten_dg: 0,
    angestelltenbetten_ug: 0,
    angestelltenbetten_eg: 0,
    angestelltenbetten_og: 0,
    angestelltenbetten_dg: 0
  },
  schutzziel: {
    gefaehrdungsbild: ''
  },
  vormeinung: {
    positiv: true,
    negativ: false
  },
  verteiler: {
    eigentuemer: true,
    gemeinde: true,
    kanton: true
  },
  naechste_inspektion: '',
  massnahmen: []
};

export default function NewInspection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [filteredChecklists, setFilteredChecklists] = useState([]);
  
  const [formData, setFormData] = useState({
    property_address: '',
    property_name: '',
    property_contact_name: '',
    property_contact_email: '',
    client_name: '',
    client_contact: '',
    building_type: '',
    inspection_type: 'yearly',
    checklist_id: '',
    notes: '',
  });

  // Mittlere Höhe Extras State
  const [mittlereHoeheExtras, setMittlereHoeheExtras] = useState(DEFAULT_MITTLERE_HOEHE_EXTRAS);

  // Prüfen ob Mittlere Höhe Checkliste ausgewählt ist
  const isMittlereHoehe = formData.checklist_id === 'mittlere-hoehe-11-30m';

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (checklists.length > 0 && formData.inspection_type) {
      // Filtere strikt nach inspection_type
      const filtered = checklists.filter(c => c.inspection_type === formData.inspection_type);
      setFilteredChecklists(filtered);
    }
  }, [checklists, formData.inspection_type]);

  // Auto-select checklist when building type changes or filtered list changes
  useEffect(() => {
    if (filteredChecklists.length === 0) return;
    
    // Wenn ein Gebäudetyp ausgewählt ist, suche passende Checkliste
    if (formData.building_type && formData.building_type !== 'Alle Gebäudetypen') {
      const specific = filteredChecklists.find(c => c.building_type === formData.building_type);
      if (specific) {
        setFormData(prev => ({ ...prev, checklist_id: specific.id }));
        return;
      }
    }
    
    // Sonst wähle die erste Checkliste
    if (!formData.checklist_id || !filteredChecklists.some(c => c.id === formData.checklist_id)) {
      setFormData(prev => ({ ...prev, checklist_id: filteredChecklists[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredChecklists, formData.building_type]);

  const fetchInitialData = async () => {
    try {
      const [typesRes, inspTypesRes, checklistsRes] = await Promise.all([
        typesApi.getBuildingTypes(),
        typesApi.getInspectionTypes(),
        checklistsApi.getAll()
      ]);
      setBuildingTypes(typesRes.data);
      setInspectionTypes(inspTypesRes.data);
      setChecklists(checklistsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Wenn Mittlere Höhe, dann Extras mitsenden
      const submitData = {
        ...formData,
        mittlere_hoehe_extras: isMittlereHoehe ? mittlereHoeheExtras : null
      };
      
      // Objektdaten aus dem Formular in die Extras übernehmen
      if (isMittlereHoehe) {
        submitData.mittlere_hoehe_extras.objekt_daten = {
          ...mittlereHoeheExtras.objekt_daten,
          objekt: formData.property_name || mittlereHoeheExtras.objekt_daten.objekt,
          eigentuemer: formData.client_name || mittlereHoeheExtras.objekt_daten.eigentuemer,
          telefon: formData.client_contact || mittlereHoeheExtras.objekt_daten.telefon,
        };
      }
      
      const response = await inspectionsApi.create(submitData);
      navigate(`/inspections/${response.data.id}`);
    } catch (error) {
      console.error('Error creating inspection:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Mittlere Höhe Extras Update-Funktionen
  const updateMHExtras = (section, field, value) => {
    setMittlereHoeheExtras(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateMHRootField = (field, value) => {
    setMittlereHoeheExtras(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Berechnungen für Mittlere Höhe
  const totalGeschosse = 
    (mittlereHoeheExtras.building_definition.geschosse_ug || 0) +
    (mittlereHoeheExtras.building_definition.geschosse_eg || 0) +
    (mittlereHoeheExtras.building_definition.geschosse_og || 0) +
    (mittlereHoeheExtras.building_definition.geschosse_dg || 0);
  
  const totalGaestebetten = 
    (mittlereHoeheExtras.building_definition.gaestebetten_ug || 0) +
    (mittlereHoeheExtras.building_definition.gaestebetten_eg || 0) +
    (mittlereHoeheExtras.building_definition.gaestebetten_og || 0) +
    (mittlereHoeheExtras.building_definition.gaestebetten_dg || 0);
  
  const totalAngestelltenbetten = 
    (mittlereHoeheExtras.building_definition.angestelltenbetten_ug || 0) +
    (mittlereHoeheExtras.building_definition.angestelltenbetten_eg || 0) +
    (mittlereHoeheExtras.building_definition.angestelltenbetten_og || 0) +
    (mittlereHoeheExtras.building_definition.angestelltenbetten_dg || 0);

  const selectedChecklist = checklists.find(c => c.id === formData.checklist_id);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom duration-500" data-testid="new-inspection-page">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl">Neue Abnahme</h1>
            <p className="text-muted-foreground">Erstellen Sie eine neue Brandschutz-Abnahme</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Inspection Type Selection */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="h-5 w-5 text-primary" />
                Art der Kontrolle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup 
                value={formData.inspection_type} 
                onValueChange={(value) => handleChange('inspection_type', value)}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
              >
                {inspectionTypes.map((type) => (
                  <div key={type.id} className="relative">
                    <RadioGroupItem value={type.id} id={type.id} className="peer sr-only" />
                    <Label
                      htmlFor={type.id}
                      className="flex flex-col gap-1 p-4 rounded-lg border-2 cursor-pointer transition-all
                        peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5
                        hover:border-primary/50"
                    >
                      <span className="font-medium">{type.name}</span>
                      <span className="text-sm text-muted-foreground">{type.description}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Property Information */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-primary" />
                Objekt-Informationen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="property_name">Objektbezeichnung</Label>
                <Input
                  id="property_name"
                  placeholder="z.B. Lindner Hotel & Alpentherme, Wohnüberbauung Thermalbad"
                  value={formData.property_name}
                  onChange={(e) => handleChange('property_name', e.target.value)}
                  className="h-12"
                  data-testid="property-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="property_address">Adresse *</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="property_address"
                    placeholder="Dorfstrasse 15, 3954 Leuk"
                    value={formData.property_address}
                    onChange={(e) => handleChange('property_address', e.target.value)}
                    required
                    className="h-12 pl-10"
                    data-testid="property-address-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="property_contact_name">Ansprechpartner</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="property_contact_name"
                      placeholder="Max Mustermann"
                      value={formData.property_contact_name}
                      onChange={(e) => handleChange('property_contact_name', e.target.value)}
                      className="h-12 pl-10"
                      data-testid="property-contact-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="property_contact_email" className="flex items-center gap-2">
                    E-Mail für Berichtsversand
                    <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full">
                      📧 Für Erinnerungen
                    </span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="property_contact_email"
                      type="email"
                      placeholder="kontakt@hotel.ch"
                      value={formData.property_contact_email}
                      onChange={(e) => handleChange('property_contact_email', e.target.value)}
                      className="h-12 pl-10 border-orange-300 focus:border-orange-500"
                      data-testid="property-contact-email-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <strong>📧 E-Mail-Versand:</strong> An diese Adresse wird der Prüfbericht gesendet und Erinnerungen bei ablaufenden Maßnahmen-Fristen verschickt.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="building_type">Gebäudetyp *</Label>
                <Select 
                  value={formData.building_type} 
                  onValueChange={(value) => handleChange('building_type', value)}
                  required
                >
                  <SelectTrigger className="h-12" data-testid="building-type-select">
                    <SelectValue placeholder="Gebäudetyp auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildingTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {filteredChecklists.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="checklist_id">Checkliste *</Label>
                  <Select 
                    value={formData.checklist_id} 
                    onValueChange={(value) => handleChange('checklist_id', value)}
                    required
                  >
                    <SelectTrigger className="h-12" data-testid="checklist-select">
                      <SelectValue placeholder="Checkliste auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredChecklists.map((checklist) => (
                        <SelectItem key={checklist.id} value={checklist.id}>
                          {checklist.name}
                          {checklist.is_default && ' (Standard)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedChecklist && (
                    <div className="p-3 rounded-lg bg-secondary/50 text-sm">
                      <p className="text-muted-foreground">{selectedChecklist.description}</p>
                      <p className="mt-1 flex items-center gap-1 text-primary">
                        <ClipboardList className="h-4 w-4" />
                        {selectedChecklist.items?.length || 0} Prüfpunkte
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client Information */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5 text-primary" />
                Auftraggeber
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client_name">Name / Firma *</Label>
                <Input
                  id="client_name"
                  placeholder="Gemeinde Leuk / Therme AG"
                  value={formData.client_name}
                  onChange={(e) => handleChange('client_name', e.target.value)}
                  required
                  className="h-12"
                  data-testid="client-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client_contact">Kontakt</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="client_contact"
                    placeholder="+41 27 966 XX XX"
                    value={formData.client_contact}
                    onChange={(e) => handleChange('client_contact', e.target.value)}
                    className="h-12 pl-10"
                    data-testid="client-contact-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mittlere Höhe Erweiterte Daten - nur bei passender Checkliste */}
          {isMittlereHoehe && (
            <Card className="border-border/50 border-primary/30" data-testid="mittlere-hoehe-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-primary" />
                  Erweiterte Daten (Mittlere Höhe)
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Zusätzliche Informationen für den Inspektionsrapport
                </p>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" defaultValue={["objekt", "definition"]} className="space-y-2">
                  
                  {/* Sicherheitsbeauftragter */}
                  <AccordionItem value="sicherheit" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline text-sm">
                      Sicherheitsbeauftragter / Prüfer
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Name</Label>
                          <Input 
                            value={mittlereHoeheExtras.sicherheitsbeauftragter.name}
                            onChange={(e) => updateMHExtras('sicherheitsbeauftragter', 'name', e.target.value)}
                            placeholder="Max Brandschutz"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">E-Mail</Label>
                          <Input 
                            type="email"
                            value={mittlereHoeheExtras.sicherheitsbeauftragter.email}
                            onChange={(e) => updateMHExtras('sicherheitsbeauftragter', 'email', e.target.value)}
                            placeholder="max@brandschutz.ch"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Adresse</Label>
                          <Input 
                            value={mittlereHoeheExtras.sicherheitsbeauftragter.adresse}
                            onChange={(e) => updateMHExtras('sicherheitsbeauftragter', 'adresse', e.target.value)}
                            placeholder="Sicherheitsweg 5"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">PLZ / Ort</Label>
                          <Input 
                            value={mittlereHoeheExtras.sicherheitsbeauftragter.plz_ort}
                            onChange={(e) => updateMHExtras('sicherheitsbeauftragter', 'plz_ort', e.target.value)}
                            placeholder="3900 Brig"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Mobile</Label>
                          <Input 
                            value={mittlereHoeheExtras.sicherheitsbeauftragter.mobile}
                            onChange={(e) => updateMHExtras('sicherheitsbeauftragter', 'mobile', e.target.value)}
                            placeholder="079 123 45 67"
                          />
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Objektdaten */}
                  <AccordionItem value="objekt" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline text-sm">
                      Erweiterte Objektdaten
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Dossier N°</Label>
                          <Input 
                            value={mittlereHoeheExtras.objekt_daten.dossier_nr}
                            onChange={(e) => updateMHExtras('objekt_daten', 'dossier_nr', e.target.value)}
                            placeholder="D-2025-001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Baujahr</Label>
                          <Input 
                            value={mittlereHoeheExtras.objekt_daten.baujahr}
                            onChange={(e) => updateMHExtras('objekt_daten', 'baujahr', e.target.value)}
                            placeholder="1985"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Plan / Parzelle</Label>
                          <Input 
                            value={mittlereHoeheExtras.objekt_daten.plan_parzelle}
                            onChange={(e) => updateMHExtras('objekt_daten', 'plan_parzelle', e.target.value)}
                            placeholder="Parz. 1234"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Abwart / Verantwortlicher</Label>
                          <Input 
                            value={mittlereHoeheExtras.objekt_daten.abwart_verantwortlicher}
                            onChange={(e) => updateMHExtras('objekt_daten', 'abwart_verantwortlicher', e.target.value)}
                            placeholder="Peter Huber"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label className="text-sm">Anwesende Personen</Label>
                          <Input 
                            value={mittlereHoeheExtras.objekt_daten.anwesende_personen}
                            onChange={(e) => updateMHExtras('objekt_daten', 'anwesende_personen', e.target.value)}
                            placeholder="Max Muster (Eigentümer), Hans Prüfer (Inspektor)"
                          />
                        </div>
                      </div>

                      {/* Grund der Inspektion */}
                      <div className="pt-4 border-t">
                        <Label className="mb-3 block text-sm font-medium">Grund der Inspektion</Label>
                        <div className="flex gap-6">
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={mittlereHoeheExtras.inspection_reason.periodisch}
                              onCheckedChange={(checked) => updateMHExtras('inspection_reason', 'periodisch', checked)}
                            />
                            <span className="text-sm">Periodische Kontrolle</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              checked={mittlereHoeheExtras.inspection_reason.nach_instandstellung}
                              onCheckedChange={(checked) => updateMHExtras('inspection_reason', 'nach_instandstellung', checked)}
                            />
                            <span className="text-sm">Nach Instandstellung</span>
                          </div>
                        </div>
                      </div>

                      {/* Bestehende Unterlagen */}
                      <div className="pt-4 border-t">
                        <Label className="mb-3 block text-sm font-medium">Bestehende Unterlagen</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm">Brandschutzkonzept:</span>
                            <RadioGroup 
                              value={mittlereHoeheExtras.existing_documents.brandschutzkonzept === true ? 'ja' : mittlereHoeheExtras.existing_documents.brandschutzkonzept === false ? 'nein' : undefined}
                              onValueChange={(v) => updateMHExtras('existing_documents', 'brandschutzkonzept', v === 'ja')}
                              className="flex gap-4 mt-2"
                            >
                              <div className="flex items-center gap-2">
                                <RadioGroupItem value="ja" id="new-bsk-ja" />
                                <Label htmlFor="new-bsk-ja" className="text-sm">Ja</Label>
                              </div>
                              <div className="flex items-center gap-2">
                                <RadioGroupItem value="nein" id="new-bsk-nein" />
                                <Label htmlFor="new-bsk-nein" className="text-sm">Nein</Label>
                              </div>
                            </RadioGroup>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">Letzter Inspektionsbericht vom:</Label>
                            <Input 
                              type="date"
                              value={mittlereHoeheExtras.existing_documents.letzter_bericht_datum}
                              onChange={(e) => updateMHExtras('existing_documents', 'letzter_bericht_datum', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Definition der Baute */}
                  <AccordionItem value="definition" className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline text-sm">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        1.0 Definition der Baute
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 space-y-6">
                      {/* 1.1 - 1.4 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <span className="text-sm">1.1 Gebäudeabstand gem. Brandschutzvorschriften:</span>
                          <RadioGroup 
                            value={mittlereHoeheExtras.building_definition.gebaeudeabstand === true ? 'ja' : mittlereHoeheExtras.building_definition.gebaeudeabstand === false ? 'nein' : undefined}
                            onValueChange={(v) => updateMHExtras('building_definition', 'gebaeudeabstand', v === 'ja')}
                            className="flex gap-4 mt-2"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="ja" id="new-ga-ja" />
                              <Label htmlFor="new-ga-ja" className="text-sm">Ja</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="nein" id="new-ga-nein" />
                              <Label htmlFor="new-ga-nein" className="text-sm">Nein</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        <div>
                          <span className="text-sm">1.2 Feuerwehrzufahrt:</span>
                          <RadioGroup 
                            value={mittlereHoeheExtras.building_definition.feuerwehrzufahrt}
                            onValueChange={(v) => updateMHExtras('building_definition', 'feuerwehrzufahrt', v)}
                            className="flex gap-4 mt-2"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="ja" id="new-fwz-ja" />
                              <Label htmlFor="new-fwz-ja" className="text-sm">Ja</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="nein" id="new-fwz-nein" />
                              <Label htmlFor="new-fwz-nein" className="text-sm">Nein</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="abgelegen" id="new-fwz-abg" />
                              <Label htmlFor="new-fwz-abg" className="text-sm">Abgelegen</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        <div>
                          <span className="text-sm">1.3 Hydranten (in 100m / mind. 7 bar):</span>
                          <RadioGroup 
                            value={mittlereHoeheExtras.building_definition.hydranten === true ? 'ja' : mittlereHoeheExtras.building_definition.hydranten === false ? 'nein' : undefined}
                            onValueChange={(v) => updateMHExtras('building_definition', 'hydranten', v === 'ja')}
                            className="flex gap-4 mt-2"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="ja" id="new-hy-ja" />
                              <Label htmlFor="new-hy-ja" className="text-sm">Ja</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="nein" id="new-hy-nein" />
                              <Label htmlFor="new-hy-nein" className="text-sm">Nein</Label>
                            </div>
                          </RadioGroup>
                        </div>

                        <div>
                          <span className="text-sm">1.4 Bauart:</span>
                          <RadioGroup 
                            value={mittlereHoeheExtras.building_definition.bauart}
                            onValueChange={(v) => updateMHExtras('building_definition', 'bauart', v)}
                            className="flex gap-4 mt-2"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="RF1" id="new-ba-rf1" />
                              <Label htmlFor="new-ba-rf1" className="text-sm">RF1</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="RF2" id="new-ba-rf2" />
                              <Label htmlFor="new-ba-rf2" className="text-sm">RF2</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="RF3" id="new-ba-rf3" />
                              <Label htmlFor="new-ba-rf3" className="text-sm">RF3</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      </div>

                      {/* 1.5 Geschosszahl */}
                      <div className="pt-4 border-t">
                        <Label className="mb-3 block text-sm font-medium">1.5 Geschosszahl</Label>
                        <div className="grid grid-cols-5 gap-2 text-center text-xs">
                          <div className="font-medium">UG</div>
                          <div className="font-medium">EG</div>
                          <div className="font-medium">OG</div>
                          <div className="font-medium">DG</div>
                          <div className="font-medium">Total</div>
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.geschosse_ug}
                            onChange={(e) => updateMHExtras('building_definition', 'geschosse_ug', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.geschosse_eg}
                            onChange={(e) => updateMHExtras('building_definition', 'geschosse_eg', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.geschosse_og}
                            onChange={(e) => updateMHExtras('building_definition', 'geschosse_og', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.geschosse_dg}
                            onChange={(e) => updateMHExtras('building_definition', 'geschosse_dg', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <div className="flex items-center justify-center font-bold bg-secondary/50 rounded h-9">
                            {totalGeschosse}
                          </div>
                        </div>
                      </div>

                      {/* 1.6 Gästebetten */}
                      <div className="pt-4 border-t">
                        <Label className="mb-3 block text-sm font-medium flex items-center gap-2">
                          <Bed className="h-4 w-4" />
                          1.6 Gästebetten
                        </Label>
                        <div className="grid grid-cols-5 gap-2 text-center text-xs">
                          <div className="font-medium">UG</div>
                          <div className="font-medium">EG</div>
                          <div className="font-medium">OG</div>
                          <div className="font-medium">DG</div>
                          <div className="font-medium">Total</div>
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.gaestebetten_ug}
                            onChange={(e) => updateMHExtras('building_definition', 'gaestebetten_ug', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.gaestebetten_eg}
                            onChange={(e) => updateMHExtras('building_definition', 'gaestebetten_eg', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.gaestebetten_og}
                            onChange={(e) => updateMHExtras('building_definition', 'gaestebetten_og', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.gaestebetten_dg}
                            onChange={(e) => updateMHExtras('building_definition', 'gaestebetten_dg', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <div className="flex items-center justify-center font-bold bg-secondary/50 rounded h-9">
                            {totalGaestebetten}
                          </div>
                        </div>
                      </div>

                      {/* 1.7 Angestelltenbetten */}
                      <div className="pt-4 border-t">
                        <Label className="mb-3 block text-sm font-medium">1.7 Angestelltenbetten</Label>
                        <div className="grid grid-cols-5 gap-2 text-center text-xs">
                          <div className="font-medium">UG</div>
                          <div className="font-medium">EG</div>
                          <div className="font-medium">OG</div>
                          <div className="font-medium">DG</div>
                          <div className="font-medium">Total</div>
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.angestelltenbetten_ug}
                            onChange={(e) => updateMHExtras('building_definition', 'angestelltenbetten_ug', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.angestelltenbetten_eg}
                            onChange={(e) => updateMHExtras('building_definition', 'angestelltenbetten_eg', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.angestelltenbetten_og}
                            onChange={(e) => updateMHExtras('building_definition', 'angestelltenbetten_og', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <Input 
                            type="number"
                            min="0"
                            value={mittlereHoeheExtras.building_definition.angestelltenbetten_dg}
                            onChange={(e) => updateMHExtras('building_definition', 'angestelltenbetten_dg', parseInt(e.target.value) || 0)}
                            className="text-center h-9"
                          />
                          <div className="flex items-center justify-center font-bold bg-secondary/50 rounded h-9">
                            {totalAngestelltenbetten}
                          </div>
                        </div>
                        <div className="mt-3 text-right">
                          <span className="font-bold text-sm">Total Betten: {totalGaestebetten + totalAngestelltenbetten}</span>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                </Accordion>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5 text-primary" />
                Bemerkungen
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Zusätzliche Informationen zur Abnahme..."
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={4}
                data-testid="notes-textarea"
              />
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Button 
              type="button" 
              variant="outline" 
              className="flex-1 h-12"
              onClick={() => navigate(-1)}
            >
              Abbrechen
            </Button>
            <Button 
              type="submit" 
              className="flex-1 h-12"
              disabled={loading || !formData.building_type || !formData.checklist_id}
              data-testid="create-inspection-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Erstellen...
                </>
              ) : (
                'Abnahme erstellen'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
